import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import InputAdornment from "@mui/material/InputAdornment";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { SearchIcon } from "@/shared/ui/console-icons";
import {
  getThroughputLineOptionKey,
  type ThroughputLineTrendData,
  type ThroughputLineOption,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";
import { getThroughputLineDisplayName } from "@/features/deployment-dashboard/widgets/charts/throughput-trend/throughput-trend-chart.utils";

interface ThroughputLineSelectorProps {
  lines: ThroughputLineOption[];
  lineTrendData: ThroughputLineTrendData[];
  selectedKeys: ReadonlySet<string>;
  onSelectionChange: (keys: ReadonlySet<string>) => void;
  onLineHover?: (key: string | null) => void;
  loading?: boolean;
  disabled?: boolean;
}

/** Side panel for selecting configured throughput lines and viewing their totals. */
export function ThroughputLineSelector({
  lines,
  lineTrendData,
  selectedKeys,
  onSelectionChange,
  onLineHover,
  loading = false,
  disabled = false,
}: ThroughputLineSelectorProps) {
  const [search, setSearch] = useState("");
  const theme = useTheme();

  const hasMultipleAreas = useMemo(
    () => new Set(lines.map((line) => line.areaId)).size > 1,
    [lines]
  );

  const filteredLines = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return lines;
    return lines.filter(
      (line) =>
        getThroughputLineDisplayName(line.line_name).toLowerCase().includes(term) ||
        (hasMultipleAreas && line.areaName.toLowerCase().includes(term))
    );
  }, [lines, search, hasMultipleAreas]);

  const trendByKey = useMemo(() => {
    const map = new Map<string, ThroughputLineTrendData>();
    for (const data of lineTrendData) {
      map.set(getThroughputLineOptionKey(data.line), data);
    }
    return map;
  }, [lineTrendData]);

  const isDisabled = disabled || loading;

  function handleToggle(key: string) {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        borderRight: { xs: 0, md: 1 },
        borderBottom: { xs: 1, md: 0 },
        borderColor: "divider",
        pr: { xs: 0, md: 1.5 },
        pb: { xs: 1.5, md: 0 },
      }}
    >
      <TextField
        size="small"
        placeholder="Search lines"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        disabled={isDisabled}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "1rem" }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 0.5 }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto auto auto",
          alignItems: "center",
          columnGap: 1,
        }}
      >
        <Box sx={{ width: 28 }} />
        <Typography variant="caption" color="text.secondary">
          Line Name
        </Typography>
        <Typography
          variant="caption"
          sx={{ minWidth: 40, textAlign: "right", color: theme.palette.success.main }}
        >
          In
        </Typography>
        <Typography
          variant="caption"
          sx={{ minWidth: 40, textAlign: "right", color: theme.palette.error.main }}
        >
          Out
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 40, textAlign: "right" }}
        >
          Net
        </Typography>
      </Box>
      <Divider sx={{ my: 0.5 }} />
      <Box sx={{ overflowY: "auto", flex: "1 1 0" }}>
        {filteredLines.map((line) => {
          const key = getThroughputLineOptionKey(line);
          const trend = trendByKey.get(key);
          return (
            <Box
              key={key}
              onMouseEnter={() => {
                if (!isDisabled) onLineHover?.(key);
              }}
              onMouseLeave={() => {
                if (!isDisabled) onLineHover?.(null);
              }}
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto auto auto",
                alignItems: "center",
                columnGap: 1,
                py: 0.5,
              }}
            >
              <Checkbox
                size="small"
                checked={selectedKeys.has(key)}
                onChange={() => handleToggle(key)}
                disabled={isDisabled}
                slotProps={{
                  input: { "aria-label": getThroughputLineDisplayName(line.line_name) },
                }}
                sx={{ p: 0.5 }}
              />
              <Box
                onClick={() => {
                  if (!isDisabled) handleToggle(key);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                  cursor: isDisabled ? "default" : "pointer",
                }}
              >
                <Typography variant="body2" noWrap>
                  {getThroughputLineDisplayName(line.line_name)}
                </Typography>
                {hasMultipleAreas && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {line.areaName}
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{ minWidth: 40, textAlign: "right", color: theme.palette.success.main }}
              >
                {trend != null && !trend.loading && trend.points.some((point) => point.in != null)
                  ? trend.totalIn.toLocaleString("en-GB")
                  : "–"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ minWidth: 40, textAlign: "right", color: theme.palette.error.main }}
              >
                {trend != null && !trend.loading && trend.points.some((point) => point.out != null)
                  ? trend.totalOut.toLocaleString("en-GB")
                  : "–"}
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ minWidth: 40, textAlign: "right" }}
              >
                {trend != null &&
                !trend.loading &&
                trend.points.some((point) => point.in != null) &&
                trend.points.some((point) => point.out != null)
                  ? trend.net.toLocaleString("en-GB")
                  : "–"}
              </Typography>
            </Box>
          );
        })}
        {filteredLines.length === 0 && search !== "" && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
            No lines match &ldquo;{search}&rdquo;
          </Typography>
        )}
      </Box>
    </Box>
  );
}
